import { firebaseFuncs } from './firebase'
import { Cart, CartItem } from '../models/Cart'
import {
  Functions,
  DeliveryRegion,
  Collection,
  OrderStatus,
  OrderItemStatus,
  Countries
} from '../config/enums'
import { Order, orderConverter } from '../models/Order'
import { DeliveryOption } from '../models/Item'
import { Address } from '../models/Recipient'
import { firebaseDb } from './firebase'
import { convertDateFormat } from '../utility/stringUtils'
import phProvinces from '../config/ph-provinces'

interface OrderItemParam {
  itemId: string
  itemVariant: string
  quantity: number
  giftId?: string
  months?: number
}

const cartItemsToParams = (cartItems: CartItem[], months?: number) => {
  let params: OrderItemParam[] = []
  cartItems.forEach(cartItem => {
    let param: OrderItemParam = {
      itemId: cartItem.item.id,
      itemVariant: cartItem.variant.description,
      quantity: cartItem.quantity
    }

    if (cartItem.gift) {
      param.giftId = cartItem.gift.id
    }
    if ((months ?? 0) > 1) {
      param.months = months
    }

    params.push(param)
  })

  return params
}

const responseOrderConverter = (order: Order, orderId: string) => {
  const orderCopy = { ...order }

  // Assign id as this is not returned to us automatically
  orderCopy.id = orderId

  // Backend will return dates as string so need to convert them back to be useful to us
  orderCopy.orderItems.forEach(orderItem => {
    if (orderItem.shippingInfo) {
      orderItem.shippingInfo.minArrivalDate = new Date(
        String(orderItem.shippingInfo.minArrivalDate)
      )
      orderItem.shippingInfo.maxArrivalDate = new Date(
        String(orderItem.shippingInfo.maxArrivalDate)
      )
    }
  })

  return orderCopy
}

export const updateCartItems = async (cart: Cart) => {
  let params: any = {
    orderItems: cartItemsToParams(cart.items, cart.months)
  }

  if (cart.order) {
    params.orderId = cart.order.id
  }

  const updateCartItemsFunc = firebaseFuncs.httpsCallable(
    Functions.updateCartItems
  )
  const res = await updateCartItemsFunc(params)

  return responseOrderConverter(res.data.order, res.data.orderId)
}

export const updateOrder = async (order: Order, installmentTerm?: number) => {
  let hasChanges = false
  let params: any = {
    id: order.id
  }

  const shippingInfo: any[] = []
  order.orderItems.forEach(orderItem => {
    if (orderItem.shippingInfo) {
      const minDateStr =
        orderItem.shippingInfo.minArrivalDate instanceof Date
          ? orderItem.shippingInfo.minArrivalDate.toUTCString()
          : orderItem.shippingInfo.minArrivalDate

      const maxDateStr =
        orderItem.shippingInfo.maxArrivalDate instanceof Date
          ? orderItem.shippingInfo.maxArrivalDate.toUTCString()
          : orderItem.shippingInfo.maxArrivalDate

      shippingInfo.push({
        itemId: orderItem.item.id,
        courier: orderItem.shippingInfo.courier,
        fee: orderItem.shippingInfo.fee,
        minArrivalDate: minDateStr,
        maxArrivalDate: maxDateStr
      })
    }
  })

  if (shippingInfo.length) {
    params.shippingInfo = shippingInfo
    hasChanges = true
  }

  if (order.recipient) {
    params.recipient = order.recipient
    hasChanges = true
  }

  if (installmentTerm) {
    params.installmentTerm = installmentTerm
  }

  if (!hasChanges) {
    return Promise.resolve(order)
  }

  const updateOrderFunc = firebaseFuncs.httpsCallable(Functions.updateOrder)
  const res = await updateOrderFunc(params)

  return responseOrderConverter(res.data.order, order.id)
}

export const getAvailableDeliveryOptions = (
  itemId: string,
  cartItems: CartItem[],
  address: Address
): DeliveryOption[] => {
  const filtered = cartItems.filter(cartItem => cartItem.item.id === itemId)
  if (!filtered.length) {
    return []
  }

  const item = filtered[0].item
  let availableOptions: DeliveryOption[] = []

  item.deliveryOptions.forEach(deliveryOption => {
    if (deliveryOption.region === DeliveryRegion.any) {
      // Special handling: shippable to any address region
      availableOptions.push(deliveryOption)
    } else if (address.country === Countries.philippines) {
      // Special handling: get provinces based on delivery region, and match address region
      const provinces = phProvinces[deliveryOption.region.toLocaleLowerCase()]
      if (provinces) {
        const matches = provinces.filter(province => {
          return (
            province.toLocaleLowerCase() === address.region.toLocaleLowerCase()
          )
        })
        if (matches.length) {
          availableOptions.push(deliveryOption)
        }
      }
    } else {
      // Match delivery region to address region
      if (
        deliveryOption.region.toLocaleLowerCase() ===
        address.region.toLocaleLowerCase()
      ) {
        availableOptions.push(deliveryOption)
      }
    }
  })

  return availableOptions
}

export interface ViewOrderItem {
  orderNumber: string
  orderId: string
  date: string
  price: number
  isInstallment: boolean
  isInstallmentDone: boolean
  rawOrder: Order
}

export const viewAllPlacedOrders = async (userId: string, limit: number) => {
  let newArray: ViewOrderItem[] = []
  const docs = await firebaseDb
    .collection(Collection.orders)
    .where('consumerId', '==', userId)
    .where('status', 'in', [
      OrderStatus.placed,
      OrderStatus.approved,
      OrderStatus.closed,
      OrderStatus.rejected
    ])
    .orderBy('createdAt', 'desc')
    .limit(limit)
    .withConverter(orderConverter)
    .get()

  docs.forEach(doc => {
    const order = doc.data()
    console.log('order:', order)
    newArray.push({
      orderNumber: order.orderNumber,
      orderId: doc.id,
      date: convertDateFormat(order.createdAt),
      price: order.totalAmount,
      isInstallment:
        order.installmentId !== undefined &&
        order.installmentInfo !== undefined,
      isInstallmentDone:
        (order.installmentInfo?.monthsPaid ?? 0) ===
        (order.installmentInfo?.term ?? 3),
      rawOrder: order
    })
  })

  return newArray
}

export const getOrderDetail = async (orderId: string) => {
  const doc = await firebaseDb
    .collection(Collection.orders)
    .doc(orderId)
    .withConverter(orderConverter)
    .get()

  return doc.data()
}

export const updateOrderItemStatus = async (
  orderId: string,
  itemIds: string[],
  status: OrderItemStatus
) => {
  const params = {
    orderId,
    itemIds,
    status
  }

  const updateOrderItemStatusFunc = firebaseFuncs.httpsCallable(
    Functions.updateOrderItemStatus
  )

  await updateOrderItemStatusFunc(params)
}
